import React from 'react';
import { useTranslation } from 'react-i18next';
import TimeSeriesChart, { TimeSeriesPoint, TimeSeriesSeries, TimeSeriesReferenceLine } from './TimeSeriesChart';

interface MetricChartWidgetProps {
    title: string;
    subtitle?: string;
    data: TimeSeriesPoint[];
    series: TimeSeriesSeries[];
    loading?: boolean;
    height?: number;
    xFormatter?: (value: string) => string;
    yFormatter?: (value: number) => string;
    yDomainMax?: number;
    referenceLine?: TimeSeriesReferenceLine;
    // Optional extra class for the title, used to match the title size elsewhere without affecting other usages
    titleClassName?: string;
    // Set to false for whole-number series, such as counts, to avoid fractional ticks
    allowDecimals?: boolean;
}

const MetricChartWidget: React.FC<MetricChartWidgetProps> = ({
    title, subtitle, data, series, loading, height, xFormatter, yFormatter, yDomainMax, referenceLine, titleClassName, allowDecimals,
}) => {
    const { t } = useTranslation();

    return (
        <div className='metric-chart-widget'>
            <div className='metric-chart-header'>
                <h6 className={titleClassName}>{title}</h6>
                {subtitle && <span className='metric-chart-subtitle'>{subtitle}</span>}
            </div>
            {/* Show a loader, an empty state, or the chart itself, matching the given height so nothing jumps once it arrives */}
            {loading ? (
                <div className='metric-chart-loading' style={height ? { height } : undefined}>
                    <div className='loader' />
                </div>
            ) : data.length === 0 ? (
                <div className='metric-chart-empty' style={height ? { height } : undefined}>{t('general.no_data_available')}</div>
            ) : (
                <TimeSeriesChart
                    data={data}
                    series={series}
                    height={height}
                    xFormatter={xFormatter}
                    yFormatter={yFormatter}
                    yDomainMax={yDomainMax}
                    referenceLine={referenceLine}
                    allowDecimals={allowDecimals}
                />
            )}
        </div>
    );
};

export default MetricChartWidget;
